import {endOfDay, format, getDay, isWithinInterval} from 'date-fns'
import {observer} from 'mobx-react-lite'
import React from 'react'
import {Time} from 'yougile-util'
import {TaskType, useCalendarStore} from '../../calendar-store'
import {minTaskHeight, validDateFormat} from '../../utils'
import {useDraggableStore} from '../draggable/draggable-store'
import {TaskText} from '../task/task-text'
import {PlaceholderTw} from '../week'

export const daysHeaderBottomLine = (
  <div
    className="h-3 text-input-disabled-old"
    style={{
      background: `repeating-linear-gradient(to right, currentColor 0, currentColor 3px, transparent 3px)`
    }}
  />
)

const taskHeight = minTaskHeight
const dateFormat = 'yyyy-MM-dd'

export const AllDayPlaceholder = observer(
  // eslint-disable-next-line max-lines-per-function
  ({start, width}: {start: Date; width: number}) => {
    const [draggableState] = useDraggableStore()
    const [, store] = useCalendarStore()

    const types: TaskType[] = [
      'all-day-multiple',
      'all-day-single',
      'all-day-with-time',
      'from-hour-to-day'
    ]

    const dragCondition = types.some(type =>
      draggableState.draggedItem.taskType?.includes(type)
    )

    if (!dragCondition) {
      return null
    }

    const startDate =
      draggableState.draggedItem.taskType !== 'all-day-single'
        ? draggableState.startDate
        : draggableState.deadline
    const deadline = draggableState.deadline

    if (!deadline) {
      return null
    }

    if (!startDate) {
      return null
    }

    if (validDateFormat(startDate, dateFormat) === format(start, dateFormat)) {
      return (
        <PlaceholderTw
          style={{
            top: draggableState.draggedItem.top,
            height: taskHeight,
            width: draggableState.draggedItem.width,
            backgroundColor: draggableState.draggedItem.backgroundColor
          }}
        >
          <TaskText
            allDay={true}
            height={taskHeight}
            taskId={draggableState.draggedItem.id}
            hideDeadline={true}
            realStartDate={Time.toTimestamp(startDate)}
            realDeadline={Time.toTimestamp(deadline)}
            gtdStage={draggableState.draggedItem.gtdStage}
            roundedDeadline={deadline}
            columnColor={draggableState.draggedItem.backgroundColor}
            title={draggableState.draggedItem.title}
            isColorDark={draggableState.draggedItem.isColorDark}
            assigned={draggableState.draggedItem.assigned}
            isSubtask={draggableState.draggedItem.isSubtask}
          />
        </PlaceholderTw>
      )
    } else if (
      deadline &&
      startDate &&
      format(deadline, dateFormat) === format(start, dateFormat) &&
      !isWithinInterval(startDate, {
        start: store.startDate('current'),
        end: endOfDay(store.endDate('current'))
      })
    ) {
      const day = getDay(deadline)
      const correctDay = day === 0 ? 7 : day
      return (
        <PlaceholderTw
          style={{
            top: draggableState.draggedItem.top,
            height: taskHeight,
            width: width * correctDay,
            left: -((correctDay - 1) * width),
            backgroundColor: draggableState.draggedItem.backgroundColor
          }}
        >
          <TaskText
            allDay={true}
            height={taskHeight}
            taskId={draggableState.draggedItem.id}
            hideDeadline={true}
            realStartDate={Time.toTimestamp(startDate)}
            realDeadline={Time.toTimestamp(deadline)}
            gtdStage={draggableState.draggedItem.gtdStage}
            roundedDeadline={deadline}
            columnColor={draggableState.draggedItem.backgroundColor}
            title={draggableState.draggedItem.title}
            isColorDark={draggableState.draggedItem.isColorDark}
            assigned={draggableState.draggedItem.assigned}
            isSubtask={draggableState.draggedItem.isSubtask}
          />
        </PlaceholderTw>
      )
    } else if (
      startDate < store.startDate('current') &&
      deadline > endOfDay(store.endDate('current')) &&
      getDay(start) === 1
    ) {
      return (
        <PlaceholderTw
          style={{
            top: draggableState.draggedItem.top,
            height: taskHeight,
            width: width * 7,
            backgroundColor: draggableState.draggedItem.backgroundColor
          }}
        >
          <TaskText
            allDay={true}
            height={taskHeight}
            taskId={draggableState.draggedItem.id}
            hideDeadline={true}
            realStartDate={Time.toTimestamp(startDate)}
            realDeadline={Time.toTimestamp(deadline)}
            gtdStage={draggableState.draggedItem.gtdStage}
            roundedDeadline={deadline}
            columnColor={draggableState.draggedItem.backgroundColor}
            title={draggableState.draggedItem.title}
            isColorDark={draggableState.draggedItem.isColorDark}
            assigned={draggableState.draggedItem.assigned}
            isSubtask={draggableState.draggedItem.isSubtask}
          />
        </PlaceholderTw>
      )
    }
    return null
  }
)
